import React, { useState, useEffect } from 'react';
import { Network, RefreshCw, AlertTriangle, CheckCircle, Activity, Plus, Trash2, Settings, BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';

const LoadBalancing = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showAddServer, setShowAddServer] = useState(false);
  const [newServer, setNewServer] = useState({
    name: '',
    url: '',
    weight: 1,
    maxConnections: 100,
    balancer: 'default'
  });
  const [addingServer, setAddingServer] = useState(false);
  const [removingServer, setRemovingServer] = useState(null);

  // API functions
  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/balancer/config', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setConfig(result.data);
        setError(null);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch load balancer configuration');
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  };

  const addServer = async () => {
    if (!newServer.name || !newServer.url) {
      toast.error('Server name and URL are required');
      return;
    }

    setAddingServer(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/balancer/servers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newServer)
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Server added to load balancer successfully');
        setNewServer({ name: '', url: '', weight: 1, maxConnections: 100, balancer: 'default' });
        setShowAddServer(false);
        await fetchConfig();
      } else {
        toast.error(result.message || 'Failed to add server');
      }
    } catch (err) {
      toast.error('Failed to add server');
      console.error('Error adding server:', err);
    } finally {
      setAddingServer(false);
    }
  };

  const removeServer = async (server) => {
    if (!window.confirm(`Are you sure you want to remove "${server.name}" from the load balancer?`)) {
      return;
    }

    setRemovingServer(server.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/proxy/balancer/servers/${server.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: server.url, balancer: server.balancer })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Server removed from load balancer successfully');
        await fetchConfig();
      } else {
        toast.error(result.message || 'Failed to remove server');
      }
    } catch (err) {
      toast.error('Failed to remove server');
      console.error('Error removing server:', err);
    } finally {
      setRemovingServer(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'inactive': return 'text-red-400';
      case 'maintenance': return 'text-yellow-400';
      default: return 'text-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'inactive': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  useEffect(() => {
    fetchConfig();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchConfig, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading load balancer configuration...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center space-x-2 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Error loading load balancer configuration</span>
        </div>
        <p className="text-red-300 mt-2">{error}</p>
        <button 
          onClick={() => { setLoading(true); fetchConfig(); }}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Load Balancer Status */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Network className="w-6 h-6 text-blue-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-slate-200">Load Balancing Configuration</h2>
                <p className="text-slate-400 mt-1">Configure load balancing rules and server pools</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => { setLoading(true); fetchConfig(); }}
                disabled={loading}
                className="px-3 py-2 bg-slate-700 text-slate-200 rounded-md hover:bg-slate-600 flex items-center disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Status</span>
                <div className={`flex items-center ${config?.enabled ? 'text-green-400' : 'text-red-400'}`}>
                  {config?.enabled ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  <span className="ml-1 text-sm font-medium">{config?.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Method</span>
                <span className="text-slate-200 font-mono text-sm">{config?.method || 'round_robin'}</span>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Active Servers</span>
                <span className="text-green-400 font-semibold">{config?.servers?.filter(s => s.status === 'active').length || 0}</span>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Failed Servers</span>
                <span className="text-red-400 font-semibold">{config?.stats?.failedServers || 0}</span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Total Requests</span>
                <BarChart3 className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-2xl font-semibold text-slate-200">{config?.stats?.totalRequests?.toLocaleString() || '0'}</div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Active Connections</span>
                <Activity className="w-4 h-4 text-green-400" />
              </div>
              <div className="text-2xl font-semibold text-slate-200">{config?.stats?.activeConnections || '0'}</div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Health Checks</span>
                <CheckCircle className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-slate-200">{config?.healthChecks ? 'Enabled' : 'Disabled'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Server Pool */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Settings className="w-6 h-6 text-blue-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-slate-200">Server Pool</h2>
                <p className="text-slate-400 mt-1">Manage load balancer backend servers</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAddServer(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Server
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {config?.servers?.map(server => (
              <div key={server.id} className="bg-slate-900 border border-slate-600 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="text-lg font-medium text-slate-200">{server.name}</h3>
                      <div className={`flex items-center mt-1 ${getStatusColor(server.status)}`}>
                        {getStatusIcon(server.status)}
                        <span className="ml-2 text-sm capitalize">{server.status}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => removeServer(server)}
                      disabled={removingServer === server.id}
                      className="p-2 text-slate-400 hover:text-red-400 disabled:opacity-50"
                      title="Remove server"
                    >
                      <Trash2 className={`w-4 h-4 ${removingServer === server.id ? 'animate-pulse' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">URL:</span>
                    <div className="text-slate-200 font-mono break-all">{server.url}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Weight:</span>
                    <div className="text-slate-200">{server.weight}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Response Time:</span>
                    <div className="text-slate-200">{server.responseTime}ms</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Requests:</span>
                    <div className="text-slate-200">{server.requests?.toLocaleString() || '0'}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Errors:</span>
                    <div className="text-red-400">{server.errors || '0'}</div>
                  </div>
                </div>

                {server.balancer && (
                  <div className="mt-3 pt-3 border-t border-slate-600">
                    <div className="text-sm">
                      <span className="text-slate-400">Balancer Group:</span>
                      <span className="ml-2 text-slate-200">{server.balancer}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {(!config?.servers || config.servers.length === 0) && (
              <div className="text-center py-12">
                <Network className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-300 mb-2">No Servers Configured</h3>
                <p className="text-slate-400 mb-4">No backend servers are configured for load balancing.</p>
                <button 
                  onClick={() => setShowAddServer(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Server
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Server Modal */}
      {showAddServer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-slate-200 mb-4">Add Backend Server</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Server Name
                </label>
                <input
                  type="text"
                  value={newServer.name}
                  onChange={(e) => setNewServer({...newServer, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., App Server 1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Server URL
                </label>
                <input
                  type="text"
                  value={newServer.url}
                  onChange={(e) => setNewServer({...newServer, url: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., http://localhost:3001"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Weight
                  </label>
                  <input
                    type="number"
                    value={newServer.weight}
                    onChange={(e) => setNewServer({...newServer, weight: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="10"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Max Connections
                  </label>
                  <input
                    type="number"
                    value={newServer.maxConnections}
                    onChange={(e) => setNewServer({...newServer, maxConnections: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Balancer Group
                </label>
                <input
                  type="text"
                  value={newServer.balancer}
                  onChange={(e) => setNewServer({...newServer, balancer: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., app-cluster"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddServer(false)}
                className="px-4 py-2 text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={addServer}
                disabled={addingServer}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {addingServer ? 'Adding...' : 'Add Server'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadBalancing;